// src/components/Logs/LogDetailDrawer.jsx
import React, { useState, useEffect } from 'react';
import { useStore } from '../../store/useStore';
import {
  X, Copy, ChevronRight, AlertTriangle, CheckCircle,
  Clock, Server, Tag, Hash, Layers, Activity,
  ArrowRight, GitBranch, FileText, Zap,
} from 'lucide-react';

const LEVEL_COLORS = {
  ERROR:    '#ef4444',
  CRITICAL: '#fca5a5',
  WARN:     '#fbbf24',
  WARNING:  '#fbbf24',
  INFO:     '#60a5fa',
  DEBUG:    '#64748b',
};
const SOURCE_COLORS = {
  GCP:      '#4285f4',
  AZURE:    '#0078d4',
  MONGODB:  '#4ade80',
  DATABASE: '#a78bfa',
};

// Simulated call-flow steps derived from the log entry
function buildFlowSteps(log) {
  const src = log.source || 'UNKNOWN';
  const svc = log.service || 'service';
  const isErr = ['ERROR', 'CRITICAL'].includes(log.level);
  const isWarn = ['WARN', 'WARNING'].includes(log.level);

  const flows = {
    GCP: [
      { step: 'Load Balancer',     detail: 'Request received at GCP L7 LB',              status: 'ok' },
      { step: 'Cloud Run / GKE',   detail: `Dispatched to ${svc}`,                        status: isErr ? 'error' : 'ok' },
      { step: 'Application Layer', detail: log.message.slice(0, 60),                       status: isErr ? 'error' : isWarn ? 'warn' : 'ok' },
      { step: 'Pub/Sub / GCS',     detail: isErr ? 'Event NOT published — error upstream' : 'Event published successfully', status: isErr ? 'error' : 'ok' },
      { step: 'Cloud Logging',     detail: 'Log entry written to Stackdriver',             status: 'ok' },
    ],
    AZURE: [
      { step: 'Azure Front Door',  detail: 'Ingress via AFD / Traffic Manager',            status: 'ok' },
      { step: 'App Service',       detail: `Routed to ${svc}`,                             status: isErr ? 'error' : 'ok' },
      { step: 'Application Code',  detail: log.message.slice(0, 60),                       status: isErr ? 'error' : isWarn ? 'warn' : 'ok' },
      { step: 'Service Bus',       detail: isErr ? 'Message delivery failed' : 'Message enqueued', status: isErr ? 'error' : 'ok' },
      { step: 'Azure Monitor',     detail: 'Telemetry forwarded to Log Analytics',          status: 'ok' },
    ],
    MONGODB: [
      { step: 'Connection Pool',   detail: `Pool from ${svc}`,                             status: 'ok' },
      { step: 'Query Parser',      detail: 'Opcode / aggregate parsed',                    status: isErr ? 'error' : 'ok' },
      { step: 'Storage Engine',    detail: log.message.slice(0, 60),                       status: isErr ? 'error' : isWarn ? 'warn' : 'ok' },
      { step: 'Replication',       detail: isErr ? 'Replication lag — write concern not met' : 'Write acknowledged by secondaries', status: isErr ? 'error' : 'ok' },
      { step: 'Oplog',             detail: 'Entry appended to oplog',                      status: 'ok' },
    ],
    DATABASE: [
      { step: 'Connection Pool',   detail: `Connection from ${svc}`,                       status: 'ok' },
      { step: 'Query Planner',     detail: 'Execution plan selected',                      status: isErr ? 'error' : 'ok' },
      { step: 'Executor',          detail: log.message.slice(0, 60),                       status: isErr ? 'error' : isWarn ? 'warn' : 'ok' },
      { step: 'Buffer Cache',      detail: isErr ? 'Cache flush skipped — transaction rolled back' : 'Dirty pages written to disk', status: isErr ? 'error' : 'ok' },
      { step: 'WAL Writer',        detail: 'Transaction logged to WAL',                    status: 'ok' },
    ],
  };
  return flows[src] || flows.GCP;
}

function copyToClipboard(text) {
  navigator.clipboard?.writeText(text).catch(() => {});
}

function Badge({ text, color }) {
  return (
    <span style={{ fontSize: 10, fontWeight: 700, letterSpacing: '0.5px', padding: '2px 7px', borderRadius: 4, background: `${color}20`, color, border: `1px solid ${color}30` }}>
      {text}
    </span>
  );
}

function DetailRow({ icon: Icon, label, value, mono, copy }) {
  return (
    <div className="flex items-start gap-2 py-2" style={{ borderBottom: '1px solid rgba(255,255,255,.04)' }}>
      <Icon size={12} style={{ color: 'var(--text-3)', marginTop: 2, flexShrink: 0 }} />
      <span className="text-xs w-24 shrink-0" style={{ color: 'var(--text-3)' }}>{label}</span>
      <span className={`text-xs flex-1 break-all ${mono ? 'mono' : ''}`} style={{ color: 'var(--text-2)' }}>
        {value || '—'}
      </span>
      {copy && value && (
        <button onClick={() => copyToClipboard(value)}
          style={{ background: 'none', border: 'none', cursor: 'pointer', color: 'var(--text-3)', padding: 2, lineHeight: 0 }}
          className="hover:text-white shrink-0">
          <Copy size={10} />
        </button>
      )}
    </div>
  );
}

export default function LogDetailDrawer({ log, onClose }) {
  const { logs, alerts } = useStore();
  const [activeTab, setActiveTab] = useState('overview');
  const [copied, setCopied]       = useState(false);

  if (!log) return null;

  const levelColor  = LEVEL_COLORS[log.level]  || '#64748b';
  const sourceColor = SOURCE_COLORS[log.source] || '#64748b';
  const ts          = log.timestamp ? new Date(log.timestamp) : null;
  const flowSteps   = buildFlowSteps(log);

  // Context logs: same service within ±3 min, sorted by time
  const windowMs = 3 * 60 * 1000;
  const logTime  = ts ? ts.getTime() : Date.now();
  const ctxLogs  = logs
    .filter(l => l._id !== log._id && l.service === log.service && Math.abs(new Date(l.timestamp).getTime() - logTime) < windowMs)
    .sort((a, b) => new Date(a.timestamp) - new Date(b.timestamp))
    .slice(0, 20);

  // Related alerts: same source
  const relAlerts = alerts.filter(a => a.source === log.source || a.service === log.service).slice(0, 5);

  function copyRaw() {
    copyToClipboard(JSON.stringify(log, null, 2));
    setCopied(true);
    setTimeout(() => setCopied(false), 1500);
  }

  const TABS = [
    { id: 'overview', label: 'Overview',    icon: FileText },
    { id: 'flow',     label: 'Call Flow',   icon: GitBranch },
    { id: 'context',  label: `Context (${ctxLogs.length})`, icon: Layers },
    { id: 'meta',     label: 'Metadata',    icon: Hash },
  ];

  return (
    <>
      {/* Backdrop */}
      <div className="fixed inset-0 z-40" style={{ background: 'rgba(0,0,0,.4)' }} onClick={onClose} />

      {/* Drawer */}
      <div className="fixed top-0 right-0 h-full z-50 flex flex-col"
        style={{
          width: 480,
          background: 'var(--bg-secondary)',
          borderLeft: '1px solid var(--border)',
          boxShadow: '-24px 0 60px rgba(0,0,0,.5)',
          animation: 'slideInRight .2s ease',
        }}>

        {/* Header */}
        <div className="flex items-center gap-3 px-4 py-3 shrink-0"
          style={{ borderBottom: '1px solid var(--border)', background: 'var(--bg-tertiary)' }}>
          <div className="flex items-center gap-2 flex-1 min-w-0">
            <Badge text={log.level}   color={levelColor} />
            <Badge text={log.source}  color={sourceColor} />
            {log.service && <Badge text={log.service} color="#94a3b8" />}
            <span className="mono text-xs ml-auto shrink-0" style={{ color: 'var(--text-3)' }}>
              {ts ? ts.toLocaleTimeString() : '—'}
            </span>
          </div>
          <div className="flex items-center gap-1 shrink-0">
            <button onClick={copyRaw}
              className="flex items-center gap-1 px-2 py-1 rounded text-xs"
              style={{ background: 'var(--bg-secondary)', border: '1px solid var(--border)', color: copied ? '#22c55e' : 'var(--text-3)', cursor: 'pointer' }}>
              <Copy size={10} /> {copied ? 'Copied!' : 'Copy JSON'}
            </button>
            <button onClick={onClose}
              style={{ background: 'none', border: 'none', cursor: 'pointer', color: 'var(--text-3)', padding: 4, lineHeight: 0 }}
              className="hover:text-white">
              <X size={16} />
            </button>
          </div>
        </div>

        {/* Message */}
        <div className="px-4 py-3 shrink-0" style={{ borderBottom: '1px solid var(--border)', background: `${levelColor}08` }}>
          <div className="text-xs font-semibold mb-1" style={{ color: levelColor }}>Message</div>
          <div className="text-sm leading-relaxed" style={{ color: 'var(--text)', wordBreak: 'break-word' }}>
            {log.message}
          </div>
        </div>

        {/* Tabs */}
        <div className="flex gap-0 shrink-0" style={{ borderBottom: '1px solid var(--border)', background: 'var(--bg-tertiary)' }}>
          {TABS.map(t => {
            const Icon = t.icon;
            return (
              <button key={t.id} onClick={() => setActiveTab(t.id)}
                className="flex items-center gap-1.5 px-3 py-2.5 text-xs"
                style={{
                  fontWeight: activeTab === t.id ? 600 : 400,
                  color: activeTab === t.id ? '#60a5fa' : 'var(--text-3)',
                  borderBottom: activeTab === t.id ? '2px solid #60a5fa' : '2px solid transparent',
                  background: 'none', border: 'none', cursor: 'pointer', marginBottom: -1,
                  borderTop: 'none', borderLeft: 'none', borderRight: 'none',
                  whiteSpace: 'nowrap',
                }}>
                <Icon size={11} /> {t.label}
              </button>
            );
          })}
        </div>

        {/* Tab content */}
        <div className="flex-1 overflow-y-auto px-4 py-3">

          {/* ── Overview ─────────────────────────────────────── */}
          {activeTab === 'overview' && (
            <div>
              <DetailRow icon={Clock}  label="Timestamp" value={ts ? ts.toISOString() : '—'}    mono copy />
              <DetailRow icon={Server} label="Source"    value={log.source}                       copy />
              <DetailRow icon={Zap}    label="Level"     value={log.level}                        />
              <DetailRow icon={Tag}    label="Service"   value={log.service}                      copy />
              <DetailRow icon={Tag}    label="Region"    value={log.region}                       />
              <DetailRow icon={Hash}   label="Log ID"    value={log._id}       mono copy />
              <DetailRow icon={Hash}   label="Fingerprint" value={log.fingerprint} mono copy />
              {log.aiSummary && (
                <div className="mt-4 p-3 rounded-lg" style={{ background: 'rgba(139,92,246,.1)', border: '1px solid rgba(139,92,246,.25)' }}>
                  <div className="flex items-center gap-1.5 text-xs font-semibold mb-1.5" style={{ color: '#a78bfa' }}>
                    <Zap size={11} /> AI Summary
                  </div>
                  <p className="text-xs leading-relaxed" style={{ color: 'var(--text-2)' }}>{log.aiSummary}</p>
                </div>
              )}
              {log.rootCause && (
                <div className="mt-3 p-3 rounded-lg" style={{ background: 'rgba(239,68,68,.08)', border: '1px solid rgba(239,68,68,.25)' }}>
                  <div className="flex items-center gap-1.5 text-xs font-semibold mb-1.5" style={{ color: '#f87171' }}>
                    <AlertTriangle size={11} /> Root Cause
                  </div>
                  <p className="text-xs leading-relaxed" style={{ color: 'var(--text-2)' }}>{log.rootCause}</p>
                </div>
              )}

              {/* Related Alerts */}
              {relAlerts.length > 0 && (
                <div className="mt-4">
                  <div className="text-xs font-semibold mb-2" style={{ color: 'var(--text-3)', letterSpacing: '0.8px', textTransform: 'uppercase' }}>
                    Related Alerts ({relAlerts.length})
                  </div>
                  {relAlerts.map(a => (
                    <div key={a._id} className="flex items-center gap-2 py-2 px-3 rounded-lg mb-1.5"
                      style={{ background: 'var(--bg-tertiary)', border: '1px solid var(--border)' }}>
                      <AlertTriangle size={11} style={{ color: a.severity === 'CRITICAL' ? '#f87171' : '#fbbf24', flexShrink: 0 }} />
                      <span className="text-xs flex-1 truncate">{a.title}</span>
                      <span style={{ fontSize: 9, fontWeight: 700, padding: '1px 6px', borderRadius: 3, background: a.status === 'OPEN' ? 'rgba(239,68,68,.2)' : 'rgba(34,197,94,.15)', color: a.status === 'OPEN' ? '#f87171' : '#22c55e' }}>
                        {a.status}
                      </span>
                    </div>
                  ))}
                </div>
              )}
            </div>
          )}

          {/* ── Call Flow ────────────────────────────────────── */}
          {activeTab === 'flow' && (
            <div>
              <div className="text-xs mb-4" style={{ color: 'var(--text-3)' }}>
                Reconstructed execution path for this log event through the <span style={{ color: sourceColor }}>{log.source}</span> infrastructure.
              </div>
              <div className="relative">
                {/* Vertical guide line */}
                <div style={{ position: 'absolute', left: 15, top: 24, bottom: 24, width: 2, background: 'var(--border)', zIndex: 0 }} />

                {flowSteps.map((step, i) => {
                  const isLast   = i === flowSteps.length - 1;
                  const dotColor = step.status === 'error' ? '#ef4444' : step.status === 'warn' ? '#f59e0b' : '#22c55e';
                  return (
                    <div key={i} className="relative flex gap-4 mb-4">
                      {/* Dot */}
                      <div style={{ zIndex: 1, flexShrink: 0, marginTop: 2 }}>
                        <div style={{
                          width: 30, height: 30, borderRadius: '50%',
                          background: `${dotColor}18`,
                          border: `2px solid ${dotColor}`,
                          display: 'flex', alignItems: 'center', justifyContent: 'center',
                        }}>
                          {step.status === 'error' ? <X size={12} style={{ color: dotColor }} />
                            : step.status === 'warn' ? <AlertTriangle size={10} style={{ color: dotColor }} />
                            : <CheckCircle size={11} style={{ color: dotColor }} />}
                        </div>
                      </div>
                      {/* Content */}
                      <div className="flex-1 pt-1">
                        <div className="flex items-center gap-2 mb-0.5">
                          <span className="text-sm font-semibold">{step.step}</span>
                          <span style={{ fontSize: 9, fontWeight: 700, padding: '1px 6px', borderRadius: 3, background: `${dotColor}18`, color: dotColor, textTransform: 'uppercase' }}>
                            {step.status}
                          </span>
                          <span className="mono text-xs ml-auto" style={{ color: 'var(--text-3)' }}>
                            +{(i * 12 + Math.random() * 8).toFixed(1)}ms
                          </span>
                        </div>
                        <p className="text-xs" style={{ color: 'var(--text-3)' }}>{step.detail}</p>
                      </div>
                    </div>
                  );
                })}
              </div>

              {/* Summary */}
              <div className="mt-2 p-3 rounded-lg" style={{ background: 'var(--bg-tertiary)', border: '1px solid var(--border)' }}>
                <div className="flex items-center gap-2 text-xs">
                  <Activity size={11} style={{ color: '#60a5fa' }} />
                  <span style={{ color: 'var(--text-2)' }}>
                    Total trace duration: <span className="mono font-bold" style={{ color: 'var(--text)' }}>
                      {(flowSteps.length * 12 + 8).toFixed(1)}ms
                    </span>
                  </span>
                  <span className="ml-auto" style={{ color: flowSteps.some(s => s.status === 'error') ? '#f87171' : '#22c55e', fontWeight: 600 }}>
                    {flowSteps.some(s => s.status === 'error') ? '✕ Failed' : '✓ Completed'}
                  </span>
                </div>
              </div>
            </div>
          )}

          {/* ── Context ──────────────────────────────────────── */}
          {activeTab === 'context' && (
            <div>
              <div className="text-xs mb-3" style={{ color: 'var(--text-3)' }}>
                Logs from <span style={{ color: 'var(--text-2)', fontWeight: 600 }}>{log.service || log.source}</span> within ±3 minutes of this event.
              </div>
              {ctxLogs.length === 0 ? (
                <div className="text-xs text-center py-8" style={{ color: 'var(--text-3)' }}>
                  No related logs found in the current time window.
                  <br /><br />
                  Logs are matched by service name within ±3 minutes.
                </div>
              ) : (
                <div>
                  {ctxLogs.map((l, i) => {
                    const lc = LEVEL_COLORS[l.level] || '#64748b';
                    const sc = SOURCE_COLORS[l.source] || '#64748b';
                    const isCurrent = l._id === log._id;
                    const before = new Date(l.timestamp) < ts;
                    const diffMs = Math.abs(new Date(l.timestamp) - logTime);
                    const diffLabel = diffMs < 1000 ? `${diffMs}ms` : `${(diffMs / 1000).toFixed(1)}s`;
                    return (
                      <div key={`${l._id}-${i}`}
                        className="flex gap-2 py-2 px-3 rounded-lg mb-1.5 relative"
                        style={{
                          background: isCurrent ? `${levelColor}10` : 'var(--bg-tertiary)',
                          border: `1px solid ${isCurrent ? levelColor + '40' : 'var(--border)'}`,
                        }}>
                        {isCurrent && (
                          <div style={{ position: 'absolute', left: -1, top: 0, bottom: 0, width: 3, background: levelColor, borderRadius: '2px 0 0 2px' }} />
                        )}
                        <span className="mono text-xs shrink-0" style={{ color: 'var(--text-3)', paddingTop: 1 }}>
                          {before ? '-' : '+'}{diffLabel}
                        </span>
                        <span style={{ fontSize: 9, fontWeight: 700, padding: '1px 5px', borderRadius: 3, background: `${lc}18`, color: lc, flexShrink: 0, alignSelf: 'flex-start', marginTop: 1 }}>
                          {l.level}
                        </span>
                        <span className="text-xs flex-1" style={{ color: isCurrent ? 'var(--text)' : 'var(--text-2)', lineHeight: 1.5 }}>
                          {l.message}
                        </span>
                        {isCurrent && (
                          <span className="text-xs font-bold shrink-0" style={{ color: levelColor }}>← this</span>
                        )}
                      </div>
                    );
                  })}
                </div>
              )}
            </div>
          )}

          {/* ── Metadata ─────────────────────────────────────── */}
          {activeTab === 'meta' && (
            <div>
              {log.metadata && Object.keys(log.metadata).length > 0 ? (
                <div>
                  <div className="text-xs font-semibold mb-2" style={{ color: 'var(--text-3)', letterSpacing: '0.8px', textTransform: 'uppercase' }}>
                    Metadata Fields
                  </div>
                  {Object.entries(log.metadata).map(([k, v]) => (
                    <div key={k} className="flex gap-3 py-2 px-3 rounded-lg mb-1.5"
                      style={{ background: 'var(--bg-tertiary)', border: '1px solid var(--border)' }}>
                      <span className="text-xs font-semibold shrink-0 mono" style={{ color: '#60a5fa', minWidth: 120 }}>{k}</span>
                      <span className="text-xs mono break-all" style={{ color: 'var(--text-2)', flex: 1 }}>
                        {typeof v === 'object' ? JSON.stringify(v) : String(v)}
                      </span>
                    </div>
                  ))}
                </div>
              ) : (
                <div className="text-xs py-4" style={{ color: 'var(--text-3)' }}>No metadata attached to this log entry.</div>
              )}

              {/* Raw JSON */}
              <div className="mt-4">
                <div className="flex items-center justify-between mb-2">
                  <div className="text-xs font-semibold" style={{ color: 'var(--text-3)', letterSpacing: '0.8px', textTransform: 'uppercase' }}>Raw JSON</div>
                  <button onClick={copyRaw}
                    className="flex items-center gap-1 text-xs px-2 py-1 rounded"
                    style={{ background: 'var(--bg-primary)', border: '1px solid var(--border)', color: 'var(--text-3)', cursor: 'pointer' }}>
                    <Copy size={9} /> Copy
                  </button>
                </div>
                <pre className="text-xs mono rounded-lg p-3 overflow-x-auto"
                  style={{ background: 'var(--bg-primary)', border: '1px solid var(--border)', color: '#4ade80', fontSize: 10, lineHeight: 1.7, maxHeight: 320, overflowY: 'auto' }}>
                  {JSON.stringify(log, null, 2)}
                </pre>
              </div>
            </div>
          )}
        </div>
      </div>

      <style>{`
        @keyframes slideInRight {
          from { transform: translateX(40px); opacity: 0; }
          to   { transform: translateX(0);    opacity: 1; }
        }
      `}</style>
    </>
  );
}
